import { supabase } from '../config/supabase';
import { archiveFinancialCatalogRecord } from './FinancialCatalogService';
import { DoctorPricingRule, PricingRuleAppointmentLocation, PricingRuleAppointmentPaymentOrigin } from '../types/doctor-pricing-rule';

export interface UpsertDoctorPricingRuleInput {
  pricingRuleId?: string;
  clientId: string;
  doctorId: string;
  pricingRuleAppointmentLocation: PricingRuleAppointmentLocation;
  pricingRuleAppointmentPaymentOrigin: PricingRuleAppointmentPaymentOrigin;
  pricingRulePricePerSession: number;
  pricingRuleIsActive: boolean;
}

function normalizeDatabaseErrorMessage(error: any): string {
  const message = String(error?.message || '').toLowerCase();
  const detail = String(error?.details || '').toLowerCase();
  const hint = String(error?.hint || '').toLowerCase();
  return [message, detail, hint].join(' ');
}

export class DoctorPricingRuleService {
  static async listByClient(clientId: string): Promise<DoctorPricingRule[]> {
    const { data, error } = await supabase
      .from('doctor_pricing_rules')
      .select('*')
      .eq('clientId', clientId)
      .is('archivedAt', null)
      .order('pricingRuleUpdatedAt', { ascending: false });

    if (error) throw error;
    return (data || []) as DoctorPricingRule[];
  }

  static async upsertRule(input: UpsertDoctorPricingRuleInput): Promise<DoctorPricingRule> {
    const payload = {
      clientId: input.clientId,
      doctorId: input.doctorId,
      pricingRuleAppointmentLocation: input.pricingRuleAppointmentLocation,
      pricingRuleAppointmentPaymentOrigin: input.pricingRuleAppointmentPaymentOrigin,
      pricingRulePricePerSession: input.pricingRulePricePerSession,
      pricingRuleIsActive: input.pricingRuleIsActive,
      pricingRuleUpdatedAt: new Date().toISOString(),
    };

    const query = input.pricingRuleId
      ? supabase.from('doctor_pricing_rules').update(payload).eq('pricingRuleId', input.pricingRuleId).eq('clientId', input.clientId).is('archivedAt', null)
      : supabase.from('doctor_pricing_rules').insert({
          ...payload,
          pricingRuleCreatedAt: new Date().toISOString(),
        });

    const { data, error } = await query.select('*').single();
    if (error) {
      const normalized = normalizeDatabaseErrorMessage(error);
      if (error.code === '23505' || normalized.includes('duplicate key') || normalized.includes('unique constraint')) {
        throw new Error('Já existe uma regra com esse profissional, local e origem de pagamento.');
      }
      throw error;
    }
    return data as DoctorPricingRule;
  }

  static async toggleRuleActive(pricingRuleId: string, clientId: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('doctor_pricing_rules')
      .update({
        pricingRuleIsActive: isActive,
        pricingRuleUpdatedAt: new Date().toISOString(),
      })
      .eq('pricingRuleId', pricingRuleId)
      .eq('clientId', clientId)
      .is('archivedAt', null);

    if (error) throw error;
  }

  static async deleteRule(pricingRuleId: string, clientId: string): Promise<void> {
    await archiveFinancialCatalogRecord({ clientId, kind: 'settlement_rule', recordId: pricingRuleId });
  }

  static async getRulesForDoctor(clientId: string, doctorId: string): Promise<DoctorPricingRule[]> {
    const { data, error } = await supabase
      .from('doctor_pricing_rules')
      .select('*')
      .eq('clientId', clientId)
      .eq('doctorId', doctorId)
      .is('archivedAt', null)
      .eq('pricingRuleIsActive', true);

    if (error) throw error;
    return (data || []) as DoctorPricingRule[];
  }
}
